import React, { useCallback, useState, useRef, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import UniversalButton from "components/uiComponents/UniversalButton";

import { ReactComponent as MoreIcon } from "static/icons/options.svg";

import "./DotMenu.scss";

const DotMenu = ({ children }) => {
  const [isMenuVisible, setIsMenuVisible] = useState(false);
  const [popupPosition, setPopupPosition] = useState({ top: 0, right: 0 });
  const buttonRef = useRef(null);
  const popupRef = useRef(null);

  const calculatePosition = useCallback(() => {
    if (!buttonRef.current || !popupRef.current) return;

    const buttonRect = buttonRef.current.getBoundingClientRect();
    const popupHeight = popupRef.current.offsetHeight || 200;
    const spaceBelow = window.innerHeight - buttonRect.bottom;
    const spaceAbove = buttonRect.top;
    const minSpaceBelow = popupHeight + 20;
    const right = window.innerWidth - buttonRect.right;
    const shouldOpenUpward = spaceBelow < minSpaceBelow || (spaceAbove > spaceBelow && spaceAbove >= popupHeight + 20);
    
    let top = shouldOpenUpward 
      ? buttonRect.top - popupHeight - 4
      : buttonRect.bottom + 4;

    if (top < 10) {
      top = 10;
    }
    if (top + popupHeight > window.innerHeight - 10) {
      top = window.innerHeight - popupHeight - 10;
    }

    setPopupPosition({ top, right });
  }, []);

  const toggleMenu = useCallback(() => {
    setIsMenuVisible((prev) => !prev);
  }, []);

  useLayoutEffect(() => {
    if (isMenuVisible && popupRef.current) {
      calculatePosition();
    }
  }, [isMenuVisible, calculatePosition]);

  useLayoutEffect(() => {
    if (!isMenuVisible) return;

    const handleResize = () => calculatePosition();
    const handleScroll = () => calculatePosition();
    const handleClickOutside = (e) => {
      if (
        popupRef.current?.contains(e.target) ||
        buttonRef.current?.contains(e.target)
      ) {
        return;
      }
      setIsMenuVisible(false);
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll, true);
    document.addEventListener('mousedown', handleClickOutside);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll, true);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuVisible, calculatePosition]);

  return (
    <div className="dot-menu">
      <div ref={buttonRef}>
        <UniversalButton
          type="button"
          leftIcon={<MoreIcon />}
          size="md"
          isRounded="sm"
          labelColor="blue"
          labelHoverColor="blue"
          backgroundColor="blue"
          backgroundHoverColor="blue-light"
          onClick={toggleMenu}
          className="dot-menu__button"
          data-active={isMenuVisible}
        />
      </div>

      {isMenuVisible && createPortal(
        <div 
          ref={popupRef}
          className="dot-menu__popup"
          style={{
            top: `${popupPosition.top}px`,
            right: `${popupPosition.right}px`,
          }}
        >
          {children}
        </div>,
        document.body
      )}
    </div>
  );
};

export default DotMenu;
